import {
    DirectionalHint,
    Dropdown,
    getTheme,
    IDropdownOption,
    IDropdownProps,
    ISelectableOption,
    Label,
    ResponsiveMode,
    TooltipHost,
} from "office-ui-fabric-react";
import * as React from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { Redirect } from "react-router-dom";
import "./NavigationHeaderComponent.less";
import { Log } from "../../../services/Logger";
import { SubmittalsApiService } from "../../../services/NewformaApi/SubmittalsApiService";
import { UserDataResponse } from "../../../models/UserDataResponse";

export enum AppPage {
    FileEmail,
    FileMultipleEmail,
    FileAsActionItem,
    FileAsSubmittal,
    FileAsSubmittalReviewResponse,
    FileAsRfi,
    FileAsRfiReviewResponse,
    SendAndFile,
    FileToProjectItem,
}

export interface NavigationHeaderProps extends LocalizeContextProps {
    defaultSelectedKey: AppPage;
    onNavigationChange: (page: AppPage) => void;
    isLoggedIn: boolean;
    isActionItemsSupported: boolean;
    isSubmittalsSupported: boolean;
    isRfiSupported: boolean;
    isProjectItemSupported: boolean;
    location: string | null;
    theme: string;
    hideNoProjectsToast: () => void;
    submittalsApiService?: SubmittalsApiService;
    hideOptions?: boolean | undefined;
    userData?: UserDataResponse;
    useFluent9Surface?: boolean;
}

export interface NavigationHeaderState {
    redirectTo: string | null;
    showLogo: boolean;
    hideOptions?: boolean;
}

class NavigationHeaderComponent extends React.Component<NavigationHeaderProps, NavigationHeaderState> {
    private _lastKey: any;

    constructor(props: NavigationHeaderProps, context: NavigationHeaderState) {
        super(props, context);

        this.state = {
            redirectTo: null,
            showLogo: !(Office.context.diagnostics.platform === Office.PlatformType.OfficeOnline),
            hideOptions: true,
        };
    }

    componentDidMount() {
        if (this.props.location === "/fileemail") {
            this.setState({ redirectTo: "/filemultipleemail" });
        } else {
            this.setState({ redirectTo: this.props.location });
        }
    }

    private onNavigationChange(event: React.FormEvent<HTMLDivElement>, item: IDropdownOption | undefined): void {
        if (!item) {
            this.setState({ redirectTo: null });
            return;
        }

        Log.infoStyle(`Navigation Change:${this.state.redirectTo} Key: ${item.key}`, "red");

        this.props.onNavigationChange(item.key as AppPage);
        // In the case of navigating fronm the submittalResponse Page,
        // its possible an inflight fetch of submittal data
        // is ongoing... so lets call doAbort to terminate
        if (this._lastKey === AppPage.FileAsSubmittalReviewResponse) {
            this.props.submittalsApiService?.doAbort();
        }
        this._lastKey = item.key;
        switch (item.key) {
            case AppPage.FileMultipleEmail:
                this.setState({ redirectTo: "/filemultipleemail" });
                return;
            case AppPage.FileAsActionItem:
                this.setState({ redirectTo: "/actionitem" });
                this.props.hideNoProjectsToast();
                return;
            case AppPage.FileAsSubmittal:
                this.setState({ redirectTo: "/submittal" });
                this.props.hideNoProjectsToast();
                return;
            case AppPage.FileAsSubmittalReviewResponse:
                this.setState({ redirectTo: "/submittalreviewresponse" });
                this.props.hideNoProjectsToast();
                return;
            case AppPage.FileAsRfi:
                this.setState({ redirectTo: "/rfi" });
                this.props.hideNoProjectsToast();
                return;
            case AppPage.FileAsRfiReviewResponse:
                this.setState({ redirectTo: "/rfireviewresponse" });
                this.props.hideNoProjectsToast();
                return;
            case AppPage.FileToProjectItem:
                this.setState({ redirectTo: "/projectItem" });
                this.props.hideNoProjectsToast();
                return;
        }
    }

    private getIsAppPageEnabledBasedOnUserPermissions(page: AppPage): boolean {
        if (!this.props.userData) {
            // Enable everything if userData fails to load
            return true;
        }
        // Count null as true for old NL versions
        const isProfessionalUser = this.props.userData.professionalUser !== false;
        const isContractManager = this.props.userData.contractManager !== false;

        if (isProfessionalUser && isContractManager) {
            return true;
        }

        if (isProfessionalUser && !isContractManager) {
            return (
                page === AppPage.FileEmail ||
                page === AppPage.FileMultipleEmail ||
                page === AppPage.FileAsActionItem ||
                page === AppPage.FileToProjectItem
            );
        }

        return page === AppPage.FileEmail || page === AppPage.FileMultipleEmail;
    }

    private getDropdownOptions(): IDropdownOption[] {
        const options: IDropdownOption[] = [];

        if (!this.props.location?.includes("sendandfile")) {
            options.push({
                key: AppPage.FileMultipleEmail,
                text: this.props.translate("APP.FILE_MULTIPLE_EMAIL_HEADER") as string,
                disabled: false,
                title: "",
            });
        } else {
            options.push({
                key: AppPage.FileMultipleEmail,
                text: this.props.translate("APP.FILE_ON_SEND") as string,
                disabled: false,
                title: "",
            });
        }
        if (this.props.isActionItemsSupported) {
            options.push({
                key: AppPage.FileAsActionItem,
                text: this.props.translate("APP.FILE_AS_ACTION_ITEM_HEADER") as string,
                disabled: !this.getIsAppPageEnabledBasedOnUserPermissions(AppPage.FileAsActionItem),
                title: "",
            });
        }
        if (this.props.isSubmittalsSupported) {
            options.push({
                key: AppPage.FileAsSubmittal,
                text: this.props.translate("APP.FILE_AS_SUBMITTAL_HEADER") as string,
                disabled: !this.getIsAppPageEnabledBasedOnUserPermissions(AppPage.FileAsSubmittal),
                title: "",
            });
            options.push({
                key: AppPage.FileAsSubmittalReviewResponse,
                text: this.props.translate("APP.FILE_AS_SUBMITTAL_RESPONSE_HEADER") as string,
                disabled: !this.getIsAppPageEnabledBasedOnUserPermissions(AppPage.FileAsSubmittalReviewResponse),
                title: "",
            });
        }
        if (this.props.isRfiSupported) {
            options.push({
                key: AppPage.FileAsRfi,
                text: this.props.translate("APP.FILE_AS_RFI_HEADER") as string,
                disabled: !this.getIsAppPageEnabledBasedOnUserPermissions(AppPage.FileAsRfi),
                title: "",
            });
            options.push({
                key: AppPage.FileAsRfiReviewResponse,
                text: this.props.translate("APP.FILE_AS_RFI_REVIEW_RESPONSE_HEADER") as string,
                disabled: !this.getIsAppPageEnabledBasedOnUserPermissions(AppPage.FileAsRfiReviewResponse),
                title: "",
            });
        }
        if (this.props.isProjectItemSupported) {
            options.push({
                key: AppPage.FileToProjectItem,
                text: this.props.translate("APP.FILE_TO_PROJECT_ITEM_HEADER") as string,
                disabled: !this.getIsAppPageEnabledBasedOnUserPermissions(AppPage.FileToProjectItem),
                title: "",
            });
        }

        return options;
    }

    private onRenderOption: IDropdownProps["onRenderOption"] = (option?: ISelectableOption) => {
        if (!option) {
            return null;
        }

        if (this.props.userData && option.disabled) {
            const backgroundColor = this.props.theme === "default" ? "black" : "white";
            const color = this.props.theme === "default" ? "white" : "black";

            return (
                <TooltipHost
                    content={this.props.translate("APP.DISABLED_PAGE_TOOLTIP") as string}
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    calloutProps={{
                        styles: {
                            beak: { backgroundColor },
                            beakCurtain: { backgroundColor },
                            calloutMain: {
                                backgroundColor,
                                selectors: {
                                    "*": {
                                        color,
                                    },
                                },
                            },
                        },
                    }}
                >
                    <span style={{ color: getTheme().semanticColors.buttonTextDisabled }}>{option.text}</span>
                </TooltipHost>
            );
        }
        return <span>{option.text}</span>;
    };

    private headerElement(): JSX.Element {
        if (this.props.defaultSelectedKey === AppPage.SendAndFile) {
            return (
                <>
                    <Label className="newforma-navPrefix">{this.props.translate("APP.FILE") as string}</Label>
                    <Label className="newforma-pageTitleText">{this.props.translate("SEND_AND_FILE.HEADER")}</Label>
                </>
            );
        }
        const dropdownOptions = this.getDropdownOptions();
        const placeholder = dropdownOptions.find((x) => x.key === this.props.defaultSelectedKey);
        return (
            <>
                <Label className="newforma-navPrefix">{this.props.translate("APP.FILE") as string} </Label>
                <Dropdown
                    className="newforma-navHeader"
                    placeholder={placeholder?.text ?? ""}
                    options={dropdownOptions}
                    responsiveMode={ResponsiveMode.large}
                    onChange={this.onNavigationChange.bind(this)}
                    defaultSelectedKey={this.props.defaultSelectedKey}
                    onRenderOption={this.onRenderOption}
                />
                <span
                    id="nav-dropdown-bubble-target"
                    style={{ alignSelf: "end", marginLeft: "-12px", visibility: "hidden" }}
                ></span>
            </>
        );
    }

    render(): JSX.Element {
        const headerClassName = this.props.useFluent9Surface
            ? "newforma-navHeaderComponent newforma-navHeaderComponent--fluent9"
            : "newforma-navHeaderComponent";

        return (
            <div className={headerClassName}>
                {this.state.redirectTo ? <Redirect to={this.state.redirectTo} /> : null}
                {this.state.showLogo ? (
                    <img
                        width="32"
                        height="32"
                        src={
                            this.props.theme === "dark"
                                ? "assets/NF_white_desktop-icon.svg"
                                : "assets/NewFormaDesktopLogo.svg"
                        }
                        alt="Newforma logo"
                        className="newforma-logo"
                    />
                ) : null}
                {this.headerElement()}
            </div>
        );
    }
}

export default withLocalize(NavigationHeaderComponent);
